import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePublicSettings } from "@/hooks/useSettings";
import { usePlugins } from "@/plugins/usePlugins";
import { GraduationCap, LayoutGrid, LogOut, Crown, Shield, Coins, Sprout, Sparkles, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EarlyAccessModal } from "@/components/EarlyAccessModal";
import { useDojoMode, isDojoAllowed } from "@/plugins/vibeDojo/DojoContext";
import { NotificationsBell } from "@/components/NotificationsBell";
import { runTour } from "@/components/tour/GuidedTour";

function DojoToggle() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setDojoMode } = useDojoMode();
  // Hidden on surfaces where Dojo isn't offered (Experts, Admin, Library, etc.).
  // Where shown, it does EXACTLY what the "Enter the Dojo" CTA does: open the
  // dedicated /dojo page. It never overlays the garden on the current page.
  if (!isDojoAllowed(pathname)) return null;
  return (
    <button
      id="tour-dojo-toggle"
      onClick={() => { setDojoMode(true); navigate("/dojo"); }}
      title="Enter the Dojo"
      aria-label="Enter the Dojo"
      data-testid="dojo-toggle"
      className="group relative grid place-items-center h-9 w-9 rounded-full border border-emerald-400/50 text-emerald-500 hover:text-emerald-400 hover:border-emerald-400 hover:shadow-[0_0_0_3px_rgba(16,185,129,0.15)] transition-all duration-300"
    >
      <Sprout className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
    </button>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const [earlyOpen, setEarlyOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: settings } = usePublicSettings();
  const { isPluginEnabled } = usePlugins();
  const vibeOn = isPluginEnabled("dojo_vibe");
  const expertOn = isPluginEnabled("expertmeai");
  const enterpriseOn = isPluginEnabled("enterprise");
  const isMember = user && (user.plan_type === "scholar" || user.plan_type === "enterprise" || user.role === "admin");
  // Individual = logged-in Learner/Scholar (not enterprise, not admin). They don't
  // see the standalone Pricing or Organization nav items.
  const isIndividual = user && (user.plan_type === "learner" || user.plan_type === "scholar") && user.role !== "admin";
  const ratio = settings?.coin_usd_ratio || 5;
  const coins = user?.credit_balance ?? 0;
  const usd = (coins / ratio).toFixed(2);
  const branding = settings?.branding || {};
  const logoUrl = branding.logo_url;
  const wordmark = branding.wordmark || "Learn Me";
  const showWordmark = branding.show_wordmark !== false;
  const expertmeai = settings?.expertmeai || {};
  const expertName = expertmeai.brand_name || "ExpertMeAi";

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5 md:gap-3.5">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            {logoUrl ? (
              <img src={logoUrl} alt={wordmark} className="h-8 w-auto max-w-[180px] object-contain" data-testid="brand-logo-img" />
            ) : (
              <div className="h-8 w-8 bg-primary text-primary-foreground flex items-center justify-center">
                <GraduationCap className="h-5 w-5" />
              </div>
            )}
            {showWordmark && (
              <span className="font-display font-extrabold text-lg tracking-tight" data-testid="brand-wordmark">{wordmark}</span>
            )}
          </Link>

          <span className="h-6 w-px bg-border hidden sm:block" />

          {/* ExpertMeAi — elevated brand lockup (placeholder logo) */}
          {expertOn && (
          <Link to="/experts" className="hidden sm:flex items-center gap-2 group" data-testid="nav-expertmeai" title={expertmeai.tagline || "ExpertMeAi"}>
            <span className="h-8 w-8 bg-gradient-to-br from-amber-400 to-amber-600 text-[#1a1206] grid place-items-center shadow-sm transition-shadow group-hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28)]">
              <Landmark className="h-4 w-4" />
            </span>
            <span className="font-display font-extrabold text-lg tracking-tight text-amber-600 group-hover:text-amber-500 transition-colors">
              {expertName}
            </span>
          </Link>
          )}
        </div>

        <nav className="flex items-center gap-2 md:gap-4">
          {vibeOn && <DojoToggle />}
          {!isIndividual && (
            <Link to="/pricing" className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-2" data-testid="nav-pricing">
              Pricing
            </Link>
          )}
          {user ? (
            <>
              <NotificationsBell />
              {!isMember && (
                <Link to="/pricing" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary px-2" data-testid="wallet-balance">
                  <Coins className="h-4 w-4" /> {coins} Coins <span className="text-muted-foreground font-normal">(${usd})</span>
                </Link>
              )}
              {!isMember && (
                <Link to="/pricing" data-testid="nav-upgrade" id="tour-go-scholar">
                  <Button variant="outline" size="sm" className="rounded-none gap-1 hidden sm:flex">
                    <Crown className="h-4 w-4" /> Go Scholar
                  </Button>
                </Link>
              )}
              <Link to="/support" className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-2 hidden sm:block" data-testid="nav-support">
                Get Help
              </Link>
              <button onClick={() => {
                const key = `lm_tour_dash_${isMember ? "scholar" : "learner"}_v1`;
                if (pathname === "/dashboard") runTour(key);
                else navigate("/dashboard?tour=1");
              }} className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-2 hidden md:block" data-testid="nav-take-tour">
                Take the tour
              </button>
              {enterpriseOn && !isIndividual && (
                <>
                  <Link to="/organization" className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-2 hidden md:block" data-testid="nav-organization">
                    Organization
                  </Link>
                  {user.organization_id && (
                    <Link to="/org/library" className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-2 hidden md:block" data-testid="nav-org-library">
                      Team Library
                    </Link>
                  )}
                </>
              )}              {isMember && (
                <>
                </>
              )}
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin">
                  <Button variant="outline" size="sm" className="rounded-none gap-1">
                    <Shield className="h-4 w-4" /> Admin
                  </Button>
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard">
                <Button variant="outline" size="sm" className="rounded-none gap-1">
                  <LayoutGrid className="h-4 w-4" /> Dashboard
                </Button>
              </Link>
              <Button onClick={() => { logout(); navigate("/"); }} size="sm" variant="ghost" className="rounded-none" data-testid="logout-button">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" size="sm" className="rounded-none">Log in</Button>
              </Link>
              {/* CR-040: Early Access button hidden for now. Code left intentionally; full cleanup tracked in CR-041.
              <Button onClick={() => setEarlyOpen(true)} size="sm" className="rounded-none bg-primary hover:opacity-90" data-testid="nav-request-access">
                Request Early Access
              </Button>
              */}
            </>
          )}
        </nav>
      </div>
      <EarlyAccessModal open={earlyOpen} onOpenChange={setEarlyOpen} />
    </header>
  );
}
